import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Invoice } from '../types/myaccount';
import api from '../services/api';
import MyAccountRecentTablePanel from './MyAccountRecentTablePanel';
import { getStatusBadgeClassName } from '../utils/statusBadges';

interface MyAccountInvoiceHistoryPanelProps {
    maxInvoices?: number;
    showHeader?: boolean;
    title?: string;
}

const MyAccountInvoiceHistoryPanel: React.FC<MyAccountInvoiceHistoryPanelProps> = ({
    maxInvoices = 10,
    showHeader = true,
    title = 'Invoice History'
}) => {
    const [invoices, setInvoices] = useState<Invoice[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                setLoading(true);
                const dashboardData = await api.getAccountDashboardData();
                setInvoices(Array.isArray(dashboardData.recent_invoices) ? dashboardData.recent_invoices.slice(0, maxInvoices) : []);
            } catch (error) {
                console.error('Error fetching invoices:', error);
                setInvoices([]);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
    }, [maxInvoices]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <MyAccountRecentTablePanel
            title={title}
            showHeader={showHeader}
            loading={loading}
            data={invoices}
            maxItems={maxInvoices}
            skeletonColumns={5}
            containerClassName="order-history"
            emptyMessage="No invoices found."
            columns={[
                { key: 'invoice', header: 'Invoice #' },
                { key: 'date', header: 'Date' },
                { key: 'status', header: 'Status' },
            ]}
            getRowKey={(invoice) => invoice.id}
            renderRow={(invoice) => (
                <>
                    <td>
                        <Link
                            to={`/invoices/${invoice.invoice_number}`}
                            className="invoice-number weka-account-table-link"
                        >
                            {invoice.invoice_number}
                        </Link>
                    </td>
                    <td>{formatDate(invoice.date)}</td>
                    <td>
                        <span className={getStatusBadgeClassName('invoices', invoice.status)}>
                            {invoice.status}
                        </span>
                    </td>
                </>
            )}
        />
    );
};

export default MyAccountInvoiceHistoryPanel;
